import {
  DynamicCardTranscriptWidgetComponent,
  WorkflowVersionType,
} from 'src/slices/workflow-preview/types';

export type TranscriptComponentType =
  | 'output'
  | 'input'
  | 'execution'
  | 'indicator';

export type TranscriptMessageType =
  | 'text'
  | 'button_selection'
  | 'csat_submission'
  | 'dynamic_card_selection'
  | 'dynamic_card'
  | 'article_card'
  | 'intent_trigger'
  | 'image'
  | 'embed'
  | 'execution'
  | 'related_articles'
  | 'quick_feedback'
  | 'goto_workflow'
  | 'chat_handoff_status_message'
  | 'indicator'
  | 'execution_error'
  | 'sentiment_analysis'
  | 'ai_instructions'
  | 'content_snippet'
  | 'autoflow_tool'
  | 'custom_event'
  | 'agent_testing'
  | 'solve_ab_test_variant_assignment';

interface BaseTranscriptComponent {
  component_type: TranscriptComponentType;
  message_type: TranscriptMessageType;
  timestamp: string;
}

export interface ConversationAttachment {
  file_name?: string | null;
  file_type?: string | null;
  summary?: string | null;
}

export interface TextTranscriptComponent extends BaseTranscriptComponent {
  message?: string;
  should_render_as_markdown?: boolean;
  attachments?: ConversationAttachment[];
}

export interface AutoflowToolTranscriptComponent extends BaseTranscriptComponent {
  input: Record<string, string>;
  output: Record<string, string>;
  reasoning?: string | null;
  tool_id?: string | null;
  tool_name: string;
}

export interface CustomEventTranscriptComponent extends BaseTranscriptComponent {
  event_configuration_id: string;
  event_configuration_name: string;
  properties: Record<string, unknown>;
}

export interface QuickFeedbackTranscriptComponent extends BaseTranscriptComponent {
  additional_feedback?: string;
  document_ids: string[];
  is_useful: boolean;
}

interface RelatedArticle {
  document_id: string;
  link: string;
  title: string;
}
export interface RelatedArticlesTranscriptComponent extends BaseTranscriptComponent {
  articles: RelatedArticle[];
  quick_feedback?: QuickFeedbackTranscriptComponent;
}

export interface ArticleCardTranscriptComponent extends BaseTranscriptComponent {
  link?: string;
  preview: string;
  quick_feedback?: QuickFeedbackTranscriptComponent;
  title: string;
}

export interface ButtonSelectionTranscriptComponent extends BaseTranscriptComponent {
  from_initial_intent_id?: boolean;
  option_selected: string;
  option_selected_label?: string | null;
  options: { label: string; value: string }[];
}

export interface IntentTriggeredTranscriptComponent extends BaseTranscriptComponent {
  intent_id: string;
  intent_title: string;
  intent_workflow_id?: string;
  intent_workflow_version?: number;
  reasoning?: string | null;
}

export interface GotoWorkflowTranscriptComponent extends BaseTranscriptComponent {
  intent_id?: string;
  intent_title?: string;
  intent_workflow_id: string;
  intent_workflow_version?: number;
}

export interface AiInstructionsTranscriptComponent extends BaseTranscriptComponent {
  ai_instruction_ids: string[];
}

export interface ContentSnippetTranscriptComponent extends BaseTranscriptComponent {
  content_snippet_ids: string[];
}

export interface ExecutionErrorTranscriptComponent extends BaseTranscriptComponent {
  message?: string | null;
}

export interface ExecutionTranscriptComponent extends BaseTranscriptComponent {
  message: string;
  success?: boolean;
}

export interface AgentTestTranscriptComponent extends BaseTranscriptComponent {
  message: string;
}

export interface SolveABTestVariantAssignmentTranscriptComponent extends BaseTranscriptComponent {
  ab_test_config_id: string;
  ab_test_name: string;
  intent_workflow_id: string;
  intent_workflow_version: number;
  is_control: boolean;
  variant_name: string;
  version_type: WorkflowVersionType;
}

export type IndicatorTranscriptComponent = ExecutionTranscriptComponent;

export interface ImageTranscriptComponent extends BaseTranscriptComponent {
  description: string;
  url: string;
}

export interface EmbedTranscriptComponent extends BaseTranscriptComponent {
  embed_url: string;
}

interface Component {
  label?: string;
  value: string;
}
interface DisplayableDynamicSection {
  components: Component[];
  type: 'image' | 'content' | 'title';
}
export interface DynamicCardSelectionTranscriptComponent extends BaseTranscriptComponent {
  fields: {
    displayable_dynamic_sections: DisplayableDynamicSection[];
    value: string;
  };
}

export type DynamicCardTranscriptComponent = BaseTranscriptComponent &
  DynamicCardTranscriptWidgetComponent;

export interface CsatSubmissionTranscriptComponent extends BaseTranscriptComponent {
  additional_feedback?: string;
  csat_feedback?: string[];
  csat_resolve?: boolean;
  csat_score: string;
}

export type TranscriptComponent =
  | TextTranscriptComponent
  | RelatedArticlesTranscriptComponent
  | ButtonSelectionTranscriptComponent
  | IntentTriggeredTranscriptComponent
  | ExecutionTranscriptComponent
  | ArticleCardTranscriptComponent
  | ImageTranscriptComponent
  | EmbedTranscriptComponent
  | DynamicCardSelectionTranscriptComponent
  | DynamicCardTranscriptComponent
  | CsatSubmissionTranscriptComponent
  | QuickFeedbackTranscriptComponent
  | GotoWorkflowTranscriptComponent
  | AutoflowToolTranscriptComponent
  | AiInstructionsTranscriptComponent
  | ContentSnippetTranscriptComponent
  | ExecutionErrorTranscriptComponent
  | CustomEventTranscriptComponent
  | SolveABTestVariantAssignmentTranscriptComponent;

export type ArticleTranscriptComponent =
  | RelatedArticlesTranscriptComponent
  | ArticleCardTranscriptComponent;

export const isArticleCardComponent = (
  comp: TranscriptComponent,
): comp is ArticleCardTranscriptComponent => {
  return comp.message_type === 'article_card';
};

export const isRelatedArticlesTranscriptComponent = (
  comp: TranscriptComponent,
): comp is RelatedArticlesTranscriptComponent => {
  return comp.message_type === 'related_articles';
};

// UI types
export type MessageBubblePosition =
  | 'lone'
  | 'first-in-a-batch'
  | 'middle'
  | 'last-in-a-batch';
